import { useState, useMemo, useCallback, useEffect } from "react";
import { usePermissions } from "react-admin";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { Check as CheckIcon, Search as SearchIcon, SwapHoriz as SwapHorizIcon } from "@mui/icons-material";
import { hasPermission } from "@/utils/permissions";
import { httpClient, getPartnerAwareApiUrl } from "@/providers/dataProvider/dataProvider";
import { resolvePartner } from "@/services/partnerResolution";
import { cacheSwitchedPartnerBranding } from "@/utils/brandingSync";
import { AUTH_CONSTANTS } from "@/constants";
import { openPartnerSwitcher, closePartnerSwitcher, usePartnerSwitcherOpen } from "./partnerSwitcherStore";

// ─── Types ───────────────────────────────────────────────────────────────────

interface AccessiblePartner {
  partnerId: string; // UUID — used for both X-Partner-ID header and branding lookup
  partnerName: string;
  slug: string;
  accessLevel: string;
  isHomePartner: boolean;
  webBaseUrl?: string; // Dedicated frontend URL for this partner
}

interface AccessiblePartnersResponse {
  partners: AccessiblePartner[];
  defaultPartnerId: string;
}

// ─── Recent Partners (localStorage) ──────────────────────────────────────────

const MAX_RECENT = 5;

const getRecentPartnerIds = (): string[] => {
  try {
    const stored = localStorage.getItem(AUTH_CONSTANTS.STORAGE_KEYS.RECENT_PARTNERS);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const addRecentPartner = (partnerId: string): void => {
  try {
    const recent = getRecentPartnerIds().filter((id) => id !== partnerId);
    recent.unshift(partnerId);
    localStorage.setItem(AUTH_CONSTANTS.STORAGE_KEYS.RECENT_PARTNERS, JSON.stringify(recent.slice(0, MAX_RECENT)));
  } catch {
    // Ignore storage errors
  }
};

// ─── Active Partner (sessionStorage) ─────────────────────────────────────────

const getActivePartnerId = (): string | null => {
  return sessionStorage.getItem(AUTH_CONSTANTS.STORAGE_KEYS.ACTIVE_PARTNER_ID);
};

const setActivePartnerId = (partnerId: string): void => {
  sessionStorage.setItem(AUTH_CONSTANTS.STORAGE_KEYS.ACTIVE_PARTNER_ID, partnerId);
};

/**
 * Returns the display name of the currently active partner.
 * Reads from sessionStorage branding cache first, falls back to auth_me_data.
 */
export const getActivePartnerName = (): string | null => {
  try {
    const branding = sessionStorage.getItem(AUTH_CONSTANTS.STORAGE_KEYS.PARTNER_BRANDING);
    if (branding) {
      const parsed = JSON.parse(branding);
      if (parsed.partner_company_name) return parsed.partner_company_name;
    }
  } catch {
    // fall through
  }
  return null;
};

/**
 * Returns true when a partner's frontend lives on a different origin than the
 * current app (a dedicated-tenant deployment) — meaning we should open it in a
 * new tab instead of switching context in-place. Pure and exported so the
 * switcher and its tests exercise the same logic (#5031).
 *
 * No webBaseUrl, an unparseable URL, or localhost dev → not cross-origin.
 */
export const isCrossOriginPartner = (webBaseUrl: string | undefined, currentOrigin: string): boolean => {
  if (!webBaseUrl) return false;
  try {
    const current = new URL(currentOrigin);
    if (current.hostname === "localhost" || current.hostname === "127.0.0.1") return false;
    return new URL(webBaseUrl).origin !== current.origin;
  } catch {
    return false;
  }
};

export const PartnerSwitcherMenuItem = () => {
  const { permissions, isLoading: permLoading } = usePermissions();
  const canSwitch = hasPermission(permissions, "partner-delegation", "use");

  if (permLoading || !canSwitch) return null;

  return (
    <MenuItem onClick={openPartnerSwitcher} dense>
      <ListItemIcon>
        <SwapHorizIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText>Switch Partner</ListItemText>
    </MenuItem>
  );
};

export const PartnerSwitcherDialog = () => {
  const { permissions, isLoading: permLoading } = usePermissions();
  const canSwitch = hasPermission(permissions, "partner-delegation", "use");
  const dialogOpen = usePartnerSwitcherOpen();

  const [searchQuery, setSearchQuery] = useState("");
  const [partners, setPartners] = useState<AccessiblePartner[]>([]);
  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState(false);

  const activePartnerId = getActivePartnerId();

  // ─── Fetch accessible partners when dialog opens ─────────────────────────

  const fetchPartners = useCallback(async () => {
    setLoading(true);
    try {
      // Use the partner-aware API URL (resolved PARTNER_CONFIG.apiBaseUrl), not the
      // baked VITE_API_SERVER_URL — in a dedicated tenant the baked default points at
      // the shared platform, which 401s the tenant token's audience (#5031).
      const { json } = await httpClient(`${getPartnerAwareApiUrl()}/partners/accessible`);
      const response = json as AccessiblePartnersResponse;
      setPartners(response.partners || []);
    } catch (error) {
      console.error("[PartnerSwitcher] Failed to fetch accessible partners:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (dialogOpen && canSwitch) {
      setSearchQuery("");
      void fetchPartners();
    }
  }, [dialogOpen, canSwitch, fetchPartners]);

  const handleCloseDialog = () => {
    closePartnerSwitcher();
    setSearchQuery("");
  };

  // ─── Filter & sort partners ──────────────────────────────────────────────

  const recentIds = useMemo(() => getRecentPartnerIds(), [dialogOpen]);

  const { recentPartners, otherPartners } = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();

    const filtered = partners.filter(
      (p) => p.partnerName.toLowerCase().includes(query) || p.slug.toLowerCase().includes(query)
    );

    const recent: AccessiblePartner[] = [];
    const other: AccessiblePartner[] = [];

    filtered.forEach((p) => {
      if (recentIds.includes(p.partnerId)) {
        recent.push(p);
      } else {
        other.push(p);
      }
    });

    recent.sort((a, b) => recentIds.indexOf(a.partnerId) - recentIds.indexOf(b.partnerId));
    other.sort((a, b) => a.partnerName.localeCompare(b.partnerName));

    return { recentPartners: recent, otherPartners: other };
  }, [partners, searchQuery, recentIds]);

  // ─── Partner switch handler ──────────────────────────────────────────────

  const handleSelectPartner = async (selected: AccessiblePartner) => {
    if (selected.partnerId === activePartnerId) {
      handleCloseDialog();
      return;
    }

    setSwitching(true);

    try {
      // If the partner has a dedicated frontend on a different origin, open it in a
      // new tab instead of switching context in-place.
      if (selected.webBaseUrl && isCrossOriginPartner(selected.webBaseUrl, window.location.origin)) {
        window.open(selected.webBaseUrl, "_blank");
        handleCloseDialog();
        setSwitching(false);
        return;
      }

      // In-environment switch (same origin): set the active partner for the
      // X-Partner-ID header. Done AFTER the cross-origin early-return above so that
      // opening a dedicated partner in a new tab doesn't poison this tab's context
      // with a partner UUID from another database (#5031).
      setActivePartnerId(selected.partnerId);
      addRecentPartner(selected.partnerId);

      // Resolve the selected partner's full config (apiBaseUrl, authConfig, etc.)
      // and cache it in PARTNER_CONFIG so the data provider and auth provider
      // route to the correct API after reload.
      try {
        await resolvePartner({ slug: selected.slug });
      } catch (error) {
        console.warn("[PartnerSwitcher] Failed to resolve partner config, proceeding with reload:", error);
      }

      // Pre-warm the new partner's branding cache before reload (avoids a flash of the
      // previous partner's theme). The GET endpoint returns colors from the canonical
      // branding column under `branding`; writeBrandingCache owns the cache shape (#4116).
      try {
        const { json: branding } = await httpClient(
          `${getPartnerAwareApiUrl()}/partners/${selected.partnerId}/branding`
        );
        // Owns the parse + cache-shape + clear-on-empty (cross-tenant carryover guard) so it
        // can be unit-tested independently of this component.
        cacheSwitchedPartnerBranding(branding, selected.partnerName);
      } catch (error) {
        console.warn("[PartnerSwitcher] Failed to fetch branding for new partner:", error);
        // Non-fatal — clear branding so default theme is used
        sessionStorage.removeItem(AUTH_CONSTANTS.STORAGE_KEYS.PARTNER_BRANDING);
      }

      // Reload the app to re-initialize with new partner context
      window.location.reload();
    } catch (error) {
      console.error("[PartnerSwitcher] Failed to switch partner:", error);
      setSwitching(false);
    }
  };

  // Don't render if user lacks the delegation permission
  if (permLoading || !canSwitch) return null;

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="xs" fullWidth>
      <DialogTitle>Switch Partner</DialogTitle>
      <DialogContent>
        <TextField
          size="small"
          fullWidth
          placeholder="Search partners..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
          autoFocus
          sx={{ mb: 1, mt: 1 }}
        />

        {(loading || switching) && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {!loading && (
          <List dense>
            {/* Recent Partners */}
            {recentPartners.length > 0 && (
              <>
                <Typography variant="caption" sx={{ px: 2, py: 0.5, display: "block", color: "text.secondary" }}>
                  RECENT
                </Typography>
                {recentPartners.map((p) => (
                  <PartnerListItem
                    key={p.partnerId}
                    partner={p}
                    isActive={p.partnerId === activePartnerId}
                    disabled={switching}
                    onSelect={handleSelectPartner}
                  />
                ))}
                <Divider sx={{ my: 1 }} />
              </>
            )}

            {/* All Partners */}
            <Typography variant="caption" sx={{ px: 2, py: 0.5, display: "block", color: "text.secondary" }}>
              {searchQuery ? "SEARCH RESULTS" : "ALL PARTNERS"} ({otherPartners.length})
            </Typography>

            {otherPartners.length === 0 ? (
              <Typography variant="body2" sx={{ px: 2, py: 2, color: "text.secondary", textAlign: "center" }}>
                {searchQuery ? "No partners match your search" : "No partners available"}
              </Typography>
            ) : (
              otherPartners.map((p) => (
                <PartnerListItem
                  key={p.partnerId}
                  partner={p}
                  isActive={p.partnerId === activePartnerId}
                  disabled={switching}
                  onSelect={handleSelectPartner}
                />
              ))
            )}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ─── Partner List Item ─────────────────────────────────────────────────────

const PartnerListItem = ({
  partner,
  isActive,
  disabled,
  onSelect,
}: {
  partner: AccessiblePartner;
  isActive: boolean;
  disabled: boolean;
  onSelect: (partner: AccessiblePartner) => void;
}) => (
  <ListItemButton selected={isActive} onClick={() => onSelect(partner)} disabled={disabled}>
    <ListItemText
      primary={partner.partnerName}
      secondary={partner.isHomePartner ? `${partner.slug} (home)` : partner.slug}
    />
    {isActive && <CheckIcon fontSize="small" color="primary" />}
  </ListItemButton>
);
